import React from 'react';
import moment from 'moment';
import {SVG} from '../../../../../../constants/assetLists';
import {Controller, useFormContext} from 'react-hook-form';
import {TIMING_TYPES} from '../data';
import {AnnouncementFormValues} from '../types';
import AppMUIAutocomplete from '../../../../../../components/common/AppMUIAutoComplete';
import CustomTiming from '../../CustomTiming';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import {useQuery} from '@tanstack/react-query';
import QUERY_KEYS from '../../../../../../constants/queryKeys';
import {
  handleGetAllFormsService,
  type FormsData,
} from '../../../../../../services/operations/forms';
import {useMemo} from 'react';
import {
  CustomTimingFormValues,
  useCustomTiming,
} from '../../../../../../context/CustomTimingContextProvider';
import {DAYS, getDepartmentFilterParams} from '../../../../../../utils';
import {DateInput} from '../../CustomTiming/components/DateInput';
import AppButton from '../../../../../../components/common/AppButton';
import {useClientsContext} from '../../../../../../context/clients/ClientsContextProvider';

// Types for form options
export type FormOption = {
  title: string;
  value: string;
};

export const TIMES = Array.from({length: 48}, (_, i) => {
  const hour = String(Math.floor(i / 2)).padStart(2, '0');
  const minute = i % 2 === 0 ? '00' : '30';
  return {
    value: `${hour}:${minute}`,
    title: `${hour}:${minute}`,
  };
});

function Time() {
  const {
    control,
    setValue,
    watch,
    trigger,
    formState: {errors},
  } = useFormContext<AnnouncementFormValues>();

  const {selectedClient, selectedLocation} = useClientsContext();

  const currentUser = useMemo(() => {
    const stored = localStorage.getItem('user');
    return stored ? JSON.parse(stored) : null;
  }, []);

  const isClientSelected = selectedLocation?.isClient;
  const isBranchSelected = selectedLocation?.isBranch;
  const isSubBranchSelected = selectedLocation?.isSubBranch;

  const selectedBranchId: string[] = isClientSelected
    ? watch('branchIds')
    : isBranchSelected && selectedLocation?.id
      ? [selectedLocation.id]
      : isSubBranchSelected && selectedLocation?.branchId
        ? [selectedLocation.branchId]
        : [];
  const selectedSubBranchId: string[] = isBranchSelected
    ? watch('subBranchIds')
    : isSubBranchSelected && selectedLocation?.id
      ? [selectedLocation.id]
      : [];

  const {clearCustomTimingData, customTimingData, setCustomTimingData} =
    useCustomTiming();

  const schedulingType = watch('schedulingType');
  const announcementType = watch('announcementType');
  const isReservation = announcementType === 'Reservation';

  const isAllDay = schedulingType === 'all_day';
  const isByDayAndTime = schedulingType === 'by_day_and_time';

  const {data: allForms} = useQuery<FormsData[]>({
    queryKey: [QUERY_KEYS.GET_ALL_FORMS, selectedClient?._id, selectedBranchId],
    queryFn: () =>
      handleGetAllFormsService({
        status: 'published' as FormsData['status'],
        clientId: selectedClient?._id,
        ...(selectedBranchId.length > 0 && {branchId: selectedBranchId}),
        ...(selectedSubBranchId.length > 0 && {
          subBranchId: selectedSubBranchId,
        }),
        ...getDepartmentFilterParams(),
        formForCustomers: false,
        isAnonymous: true,
      }),
    enabled: !!selectedClient?._id && announcementType === 'Regulation',
  });

  const formsOptions: FormOption[] = useMemo(() => {
    if (allForms && allForms.length > 0) {
      const options = allForms.map((form: FormsData) => ({
        title: form.title,
        value: form._id,
      }));
      return options;
    }
    return []; // Return empty array if no forms
  }, [allForms]);

  const [showCustomTimingDialog, setShowCustomTimingDialog] =
    React.useState(false);

  const handleTimeModalChange = (value: boolean) => {
    setShowCustomTimingDialog(value);
  };

  const startTimeListboxRef = React.useRef<HTMLUListElement | null>(null);
  const endTimeListboxRef = React.useRef<HTMLUListElement | null>(null);

  const getCurrentTimeValue = () => {
    const now = moment();
    const totalMinutes = now.hours() * 60 + now.minutes();
    const index = Math.min(
      Math.max(Math.round(totalMinutes / 30), 0),
      TIMES.length - 1,
    );
    return TIMES[index].value;
  };

  const scrollListboxToValue = (
    listboxEl: HTMLUListElement | null,
    targetValue: string,
  ) => {
    if (!listboxEl || !targetValue) return;
    const index = TIMES.findIndex(item => item.value === targetValue);
    if (index === -1) return;
    const optionEl = listboxEl.children[index] as HTMLElement | undefined;
    optionEl?.scrollIntoView({block: 'start'});
  };

  const handleStartTimeOpen = () => {
    setTimeout(() => {
      scrollListboxToValue(startTimeListboxRef.current, getCurrentTimeValue());
    }, 0);
  };

  const handleEndTimeOpen = () => {
    setTimeout(() => {
      scrollListboxToValue(
        endTimeListboxRef.current,
        watch('startTime') || getCurrentTimeValue(),
      );
    }, 0);
  };

  const handleFormChange = (
    name: keyof AnnouncementFormValues,
    value: string | number | boolean,
  ) => {
    if (name === 'schedulingType') {
      clearCustomTimingData();

      setValue('schedulingType', value as string, {
        shouldDirty: true,
        shouldTouch: true,
      });

      setValue('startDate', moment().format('YYYY-MM-DD'));
      setValue('endDate', moment().format('YYYY-MM-DD'));
      setValue('startTime', '');
      setValue('endTime', '');
      setValue('time', '');
      setValue('dayOfWeek', 0);

      if (value === 'all_day' || value === 'by_day_and_time') {
        setValue('endDate', '');
      }

      if (value === 'daily_at_fixed_time' || value === 'custom') {
        setValue('startDate', '');
        setValue('endDate', '');
      }

      if (value === 'custom') {
        setShowCustomTimingDialog(true);
      }

      trigger();
    } else {
      setValue(name, value, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
      trigger(name);
    }
  };

  const openCustomTimingModal = () => {
    setShowCustomTimingDialog(true);
  };

  const isFieldVisible = (fieldName: string) => {
    switch (fieldName) {
      case 'startDate':
        return (
          schedulingType === 'default' ||
          schedulingType === 'all_day' ||
          schedulingType === 'by_day_and_time' ||
          schedulingType === 'between_days_and_hours'
        );

      case 'endDate':
        return (
          schedulingType === 'default' ||
          schedulingType === 'between_days_and_hours'
        );

      case 'startTime':
      case 'endTime':
        return (
          schedulingType === 'by_day_and_time' ||
          schedulingType === 'between_days_and_hours' ||
          schedulingType === 'daily_at_fixed_time' ||
          schedulingType === 'custom'
        );

      // ❌ no longer used in new rules
      case 'time':
      case 'dayOfWeek':
        return false;

      default:
        return true;
    }
  };

  const formattedDate = (endDate: string | Date) =>
    new Date(endDate).toLocaleDateString('he-IL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });

  const customEndLabel = () => {
    switch (customTimingData?.endCondition) {
      case 'never':
        return 'ממשיך ללא סיום';
      case 'end_date':
        return `מסתיים${formattedDate(customTimingData?.endDate || '')}`;
      case 'occurrences':
        return `מסיים לאחר ${customTimingData?.occurrences} הופעות`;
      default:
        return '';
    }
  };

  return (
    <>
      <div className="my-3 flex flex-col gap-2">
        {!isReservation && (
          <div className="flex flex-wrap items-center gap-2">
            <img
              src={SVG.clockIcon}
              alt="icon"
              className="w-[20px] h-[20px] object-contain"
            />
            <Controller
              control={control}
              name="schedulingType"
              render={({field}) => (
                <AppMUIAutocomplete
                  label="תזמון מותאם"
                  size="small"
                  options={TIMING_TYPES}
                  sx={{
                    flex: 1,
                    minWidth: '260px',
                    '& .MuiAutocomplete-popupIndicator': {
                      transform: 'none !important', // Disable rotation
                    },
                  }}
                  popupIcon={<CalendarMonthOutlinedIcon />}
                  getOptionLabel={option => option.title}
                  renderOption={(props, option, {selected}) => {
                    const {key, ...optionProps} = props;
                    return (
                      <li key={key} {...optionProps}>
                        {/* <Checkbox
                        size="small"
                        color="secondary"
                        checked={selected}
                      /> */}
                        {option.title}
                      </li>
                    );
                  }}
                  value={
                    TIMING_TYPES.find(item => item.key === field.value) || null
                  }
                  onChange={(_, newValue) => {
                    const value = newValue?.key || '';
                    handleFormChange('schedulingType', value);
                  }}
                  error={!!errors.schedulingType?.message}
                  helperText={errors.schedulingType?.message}
                />
              )}
            />
          </div>
        )}

        <div
          className={`flex gap-2 ${isReservation ? 'items-center' : 'items-start'}`}>
          {isReservation && (
            <img
              src={SVG.clockIcon}
              alt="icon"
              className="w-[20px] h-[20px] object-contain"
            />
          )}

          {isFieldVisible('startDate') && (
            <Controller
              control={control}
              name="startDate"
              render={({field}) => (
                <DateInput
                  dateFormat="EEEE, d 'ב'MMMM"
                  placeholderText="yyyy, mmm, dd"
                  selected={field.value ? new Date(field.value) : null}
                  onChange={date => field.onChange(date?.toISOString() || '')}
                  label={isAllDay ? 'יום' : isByDayAndTime ? 'ביום' : 'מ-'}
                  error={errors.startDate?.message}
                />
              )}
            />
          )}

          {isFieldVisible('endDate') && (
            <Controller
              control={control}
              name="endDate"
              render={({field}) => {
                return (
                  <DateInput
                    dateFormat="EEEE, d 'ב'MMMM"
                    placeholderText="yyyy, mmm, dd"
                    selected={field.value ? new Date(field.value) : null}
                    onChange={date => field.onChange(date?.toISOString() || '')}
                    label="עד-"
                    error={errors.endDate?.message}
                  />
                );
              }}
            />
          )}
        </div>

        {schedulingType === 'by_day_and_time' && (
          <div className="flex flex-row items-start gap-2">
            <Controller
              control={control}
              name="startTime"
              render={({field}) => (
                <AppMUIAutocomplete
                  freeSolo
                  disableClearable
                  options={TIMES.map(i => i.title)}
                  label="משעה"
                  className="w-full"
                  size="small"
                  value={field.value}
                  onOpen={handleStartTimeOpen}
                  slotProps={{listbox: {ref: startTimeListboxRef}}}
                  onChange={(_, v) => {
                    handleFormChange('startTime', v);

                    const selectedStartTime = moment(v, 'HH:mm', true);
                    if (selectedStartTime.isValid()) {
                      const defaultEndTime = selectedStartTime
                        .add(1, 'hour')
                        .format('HH:mm');

                      setValue('endTime', defaultEndTime, {
                        shouldValidate: true,
                        shouldDirty: true,
                        shouldTouch: true,
                      });
                      trigger('endTime');
                    }
                  }}
                  onInputChange={(_e, newInputValue, reason) => {
                    if (reason === 'input') {
                      const matchingType = TIMES?.find(
                        (item: any) => item?.title === newInputValue,
                      );
                      if (matchingType) {
                        setValue('startTime', matchingType.value, {
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                      } else {
                        setValue('startTime', newInputValue, {
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                      }
                    }
                  }}
                  error={!!errors.startTime?.message}
                  helperText={errors.startTime?.message}
                />
              )}
            />

            <Controller
              control={control}
              name="endTime"
              render={({field}) => (
                <AppMUIAutocomplete
                  freeSolo
                  disableClearable
                  options={TIMES.map(i => i.title)}
                  label="לשעה"
                  className="w-full"
                  size="small"
                  value={field.value}
                  onOpen={handleEndTimeOpen}
                  slotProps={{listbox: {ref: endTimeListboxRef}}}
                  onChange={(_, v) => handleFormChange('endTime', v)}
                  error={!!errors.endTime?.message}
                  helperText={errors.endTime?.message}
                  onInputChange={(_e, newInputValue, reason) => {
                    if (reason === 'input') {
                      const matchingType = TIMES?.find(
                        (item: any) => item?.title === newInputValue,
                      );
                      if (matchingType) {
                        setValue('endTime', matchingType.value, {
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                      } else {
                        setValue('endTime', newInputValue, {
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                      }
                    }
                  }}
                />
              )}
            />
          </div>
        )}

        {/* Time Range Fields - Only for between_days_and_hours */}
        {schedulingType === 'between_days_and_hours' && (
          <div className="flex flex-row items-start gap-2">
            {isFieldVisible('startTime') && (
              <Controller
                control={control}
                name="startTime"
                render={({field}) => (
                  <AppMUIAutocomplete
                    freeSolo
                    disableClearable
                    options={TIMES?.map(item => item.title)}
                    label="משעה"
                    className="w-full"
                    size="small"
                    value={field.value}
                    onOpen={handleStartTimeOpen}
                    slotProps={{listbox: {ref: startTimeListboxRef}}}
                    onChange={(_, newValue) => {
                      handleFormChange('startTime', newValue);
                    }}
                    error={!!errors.startTime?.message}
                    helperText={errors.startTime?.message}
                    onInputChange={(_e, newInputValue, reason) => {
                      if (reason === 'input') {
                        const matchingType = TIMES?.find(
                          (item: any) => item?.title === newInputValue,
                        );
                        if (matchingType) {
                          setValue('startTime', matchingType.value, {
                            shouldValidate: true,
                            shouldDirty: true,
                          });
                        } else {
                          setValue('startTime', newInputValue, {
                            shouldValidate: true,
                            shouldDirty: true,
                          });
                        }
                      }
                    }}
                  />
                )}
              />
            )}

            {isFieldVisible('endTime') && (
              <Controller
                control={control}
                name="endTime"
                render={({field}) => (
                  <AppMUIAutocomplete
                    freeSolo
                    disableClearable
                    options={TIMES?.map(item => item.title)}
                    label="לשעה"
                    className="w-full"
                    size="small"
                    value={field.value}
                    onOpen={handleEndTimeOpen}
                    slotProps={{listbox: {ref: endTimeListboxRef}}}
                    onChange={(_, newValue) => {
                      handleFormChange('endTime', newValue);
                    }}
                    error={!!errors.endTime?.message}
                    helperText={errors.endTime?.message}
                    onInputChange={(_e, newInputValue, reason) => {
                      if (reason === 'input') {
                        const matchingType = TIMES?.find(
                          (item: any) => item?.title === newInputValue,
                        );
                        if (matchingType) {
                          setValue('endTime', matchingType.value, {
                            shouldValidate: true,
                            shouldDirty: true,
                          });
                        } else {
                          setValue('endTime', newInputValue, {
                            shouldValidate: true,
                            shouldDirty: true,
                          });
                        }
                      }
                    }}
                  />
                )}
              />
            )}
          </div>
        )}

        {schedulingType === 'daily_at_fixed_time' && (
          <div className="flex flex-row items-start gap-2">
            <Controller
              control={control}
              name="startTime"
              render={({field}) => (
                <AppMUIAutocomplete
                  freeSolo
                  disableClearable
                  options={TIMES.map(i => i.title)}
                  label="משעה"
                  className="w-full"
                  size="small"
                  value={field.value}
                  onOpen={handleStartTimeOpen}
                  slotProps={{listbox: {ref: startTimeListboxRef}}}
                  onChange={(_, v) => handleFormChange('startTime', v)}
                  error={!!errors.startTime?.message}
                  helperText={errors.startTime?.message}
                  onInputChange={(_e, newInputValue, reason) => {
                    if (reason === 'input') {
                      const matchingType = TIMES?.find(
                        (item: any) => item?.title === newInputValue,
                      );
                      if (matchingType) {
                        setValue('startTime', matchingType.value, {
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                      } else {
                        setValue('startTime', newInputValue, {
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                      }
                    }
                  }}
                />
              )}
            />

            <Controller
              control={control}
              name="endTime"
              render={({field}) => (
                <AppMUIAutocomplete
                  freeSolo
                  disableClearable
                  options={TIMES.map(i => i.title)}
                  label="לשעה"
                  className="w-full"
                  size="small"
                  value={field.value}
                  onOpen={handleEndTimeOpen}
                  slotProps={{listbox: {ref: endTimeListboxRef}}}
                  onChange={(_, v) => handleFormChange('endTime', v)}
                  error={!!errors.endTime?.message}
                  helperText={errors.endTime?.message}
                  onInputChange={(_e, newInputValue, reason) => {
                    if (reason === 'input') {
                      const matchingType = TIMES?.find(
                        (item: any) => item?.title === newInputValue,
                      );
                      if (matchingType) {
                        setValue('endTime', matchingType.value, {
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                      } else {
                        setValue('endTime', newInputValue, {
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                      }
                    }
                  }}
                />
              )}
            />
          </div>
        )}

        {/* Single Time Field - Only for custom */}
        {schedulingType === 'custom' && (
          <div className="flex flex-col gap-3">
            <div className="flex items-start gap-2">
              <Controller
                control={control}
                name="startTime"
                render={({field}) => (
                  <AppMUIAutocomplete
                    freeSolo
                    disableClearable
                    options={TIMES.map(i => i.title)}
                    label="משעה"
                    className="w-full"
                    size="small"
                    value={field.value}
                    onOpen={handleStartTimeOpen}
                    slotProps={{listbox: {ref: startTimeListboxRef}}}
                    onChange={(_, v) => handleFormChange('startTime', v)}
                    error={!!errors.startTime?.message}
                    helperText={errors.startTime?.message}
                    onInputChange={(_e, newInputValue, reason) => {
                      if (reason === 'input') {
                        const matchingType = TIMES?.find(
                          (item: any) => item?.title === newInputValue,
                        );
                        if (matchingType) {
                          setValue('startTime', matchingType.value, {
                            shouldValidate: true,
                            shouldDirty: true,
                          });
                        } else {
                          setValue('startTime', newInputValue, {
                            shouldValidate: true,
                            shouldDirty: true,
                          });
                        }
                      }
                    }}
                  />
                )}
              />

              <Controller
                control={control}
                name="endTime"
                render={({field}) => (
                  <AppMUIAutocomplete
                    freeSolo
                    disableClearable
                    options={TIMES.map(i => i.title)}
                    label="לשעה"
                    className="w-full"
                    size="small"
                    value={field.value}
                    onOpen={handleEndTimeOpen}
                    slotProps={{listbox: {ref: endTimeListboxRef}}}
                    onChange={(_, v) => handleFormChange('endTime', v)}
                    error={!!errors.endTime?.message}
                    helperText={errors.endTime?.message}
                    onInputChange={(_e, newInputValue, reason) => {
                      if (reason === 'input') {
                        const matchingType = TIMES?.find(
                          (item: any) => item?.title === newInputValue,
                        );
                        if (matchingType) {
                          setValue('endTime', matchingType.value, {
                            shouldValidate: true,
                            shouldDirty: true,
                          });
                        } else {
                          setValue('endTime', newInputValue, {
                            shouldValidate: true,
                            shouldDirty: true,
                          });
                        }
                      }
                    }}
                  />
                )}
              />

              <AppMUIAutocomplete
                options={DAYS}
                label="יום"
                className="w-full"
                size="small"
                getOptionLabel={option => option.label}
                multiple
                value={
                  customTimingData?.weeklyDays &&
                  customTimingData.weeklyDays.length > 0
                    ? DAYS.filter(day =>
                        customTimingData.weeklyDays.includes(day.index),
                      ) || []
                    : []
                }
                onChange={(_, newValue) => {
                  const updatedData: CustomTimingFormValues = {
                    repeatEvery: customTimingData?.repeatEvery || 1,
                    repeatType: customTimingData?.repeatType || 'weekly',
                    monthlyDates: customTimingData?.monthlyDates || [],
                    endCondition: customTimingData?.endCondition || 'never',
                    weeklyDays:
                      newValue?.length > 0
                        ? newValue.map(day => day.index)
                        : [],
                    ...(customTimingData?.endDate && {
                      endDate: customTimingData.endDate,
                    }),
                    ...(customTimingData?.occurrences && {
                      occurrences: customTimingData.occurrences,
                    }),
                  };
                  setCustomTimingData(updatedData);
                }}
              />
            </div>

            <div className="flex items-start gap-4">
              {/* Text content */}
              <div className="flex flex-col gap-1">
                {customTimingData?.repeatEvery && (
                  <span className="text-xl font-medium">
                    חוזר על עצמו כל {customTimingData.repeatEvery}{' '}
                    {customTimingData?.repeatType === 'weekly'
                      ? 'שבועות'
                      : 'חודשים'}
                  </span>
                )}

                {customTimingData?.endCondition && (
                  <span className="text-xl font-medium">
                    {customEndLabel()}
                  </span>
                )}
              </div>

              {/* Button on LEFT side */}
              <div className="ml-auto">
                <AppButton
                  onClick={openCustomTimingModal}
                  style={{paddingInline: 15, paddingBlock: 6}}
                  variant="custom"
                  className="bg-white border border-[#CAC4D0] text-[#007AFF] text-[14px] font-medium rounded-[8px]">
                  לעדכון פרטים
                </AppButton>
              </div>
            </div>
          </div>
        )}

        {/* Forms Field - Only show when announcement type is Regulation */}
        {announcementType === 'Regulation' && (
          <div className="mt-6">
            <Controller
              control={control}
              name="forms"
              render={({field: {value}}) => (
                <div className="flex flex-col gap-2 max-w-[392px]">
                  <AppMUIAutocomplete
                    label="בחירה מתוך רשימת טפסים"
                    size="small"
                    options={formsOptions}
                    fullWidth
                    getOptionLabel={(option: FormOption) => option.title}
                    value={
                      formsOptions.find(
                        (item: FormOption) => item.value === value,
                      ) || null
                    }
                    onChange={(_, newValue: FormOption | null) => {
                      const selectedValue = newValue?.value || '';
                      handleFormChange('forms', selectedValue);
                    }}
                    error={!!errors.forms}
                    helperText={errors.forms?.message}
                    noOptionsText="אין טפסים זמינים"
                  />

                  {value && (
                    <div className="sm:w-[392px] px-3 py-2 border-[1px] border-[#8080808C] rounded-lg bg-white flex justify-between items-center gap-2">
                      <div
                        className="flex items-center gap-2 cursor-pointer hover:opacity-70 transition-opacity duration-200"
                        onClick={() => {
                          const params = new URLSearchParams();
                          if (currentUser?._id)
                            params.set('userId', currentUser._id);
                          if (currentUser?.email)
                            params.set('email', currentUser.email);
                          if (currentUser?.firstName)
                            params.set('firstName', currentUser.firstName);
                          if (currentUser?.lastName)
                            params.set('lastName', currentUser.lastName);
                          const url = `${window.location.origin}/form/${value}?${params.toString()}`;
                          window.open(url, '_blank');
                        }}>
                        <div className="w-[20px] h-[10px]">
                          <img
                            src={SVG.linkBlackIcon}
                            alt="icon"
                            className="w-full h-full object-contain"
                          />
                        </div>
                        <span className="text-xl font-medium">
                          {formsOptions.find(
                            (item: FormOption) => item.value === value,
                          )?.title ?? ''}
                        </span>
                      </div>

                      <button
                        className="w-[14px] h-[14px] hover:opacity-40 transition-opacity duration-200"
                        onClick={() => handleFormChange('forms', '')}>
                        <img
                          src={SVG.closeDarkIcon}
                          alt="icon"
                          className="w-full h-full object-contain"
                        />
                      </button>
                    </div>
                  )}
                </div>
              )}
            />
          </div>
        )}
      </div>

      <CustomTiming
        open={showCustomTimingDialog}
        onClose={() => handleTimeModalChange(false)}
        onCancel={() => {
          handleTimeModalChange(false);
          clearCustomTimingData();
          handleFormChange('schedulingType', 'default');
        }}
        onSubmit={data => {
          console.log(data);
        }}
      />
    </>
  );
}

export default Time;
